import { useEffect, useRef, useState } from 'react';

/**
 * Hook para detectar se outra aba está ativa na mesma sala
 * Usa BroadcastChannel para comunicação entre abas do mesmo navegador
 *
 * Funciona de forma passiva: apenas escuta mensagens de outras abas,
 * sem enviar heartbeat contínuo.
 *
 * @param {string} slug - Slug da sala atual (extraído da URL)
 * @param {boolean} isActive - Se a detecção está ativa
 * @returns {object} { isAnotherTabActive: boolean }
 */
export default function useMultipleTabsDetection(slug, isActive = true) {
  const [isAnotherTabActive, setIsAnotherTabActive] = useState(false);
  const channelRef = useRef(null);
  const sessionIdRef = useRef(null);
  const lastHeartbeatRef = useRef({});

  useEffect(() => {
    if (!isActive || !slug) {
      return;
    }

    // Gera ID único para esta sessão/aba
    sessionIdRef.current = `session-${Date.now()}-${Math.random()}`;

    // Cria canal de comunicação
    channelRef.current = new BroadcastChannel('guara-chat-session');

    const channel = channelRef.current;
    const sessionId = sessionIdRef.current;

    // Função para verificar se outras abas ainda estão ativas
    const checkOtherTabs = () => {
      const now = Date.now();
      const recentHeartbeats = Object.values(lastHeartbeatRef.current).filter(
        (timestamp) => now - timestamp < 3000, // Considera ativo se heartbeat < 3s
      );

      if (recentHeartbeats.length === 0) {
        setIsAnotherTabActive(false);
      }
    };

    // Handler para receber mensagens de outras abas
    const handleMessage = (event) => {
      const {
        type,
        slug: msgSlug,
        sessionId: msgSessionId,
        timestamp,
      } = event.data;

      // Ignora mensagens da própria aba
      if (msgSessionId === sessionId) {
        return;
      }

      // Se é heartbeat da mesma sala, outra aba está ativa
      if (type === 'heartbeat' && msgSlug === slug) {
        lastHeartbeatRef.current[msgSessionId] = timestamp;
        setIsAnotherTabActive(true);
      }

      // Se outra aba está se desconectando
      if (type === 'disconnect' && msgSlug === slug) {
        delete lastHeartbeatRef.current[msgSessionId];
        checkOtherTabs();
      }
    };

    channel.addEventListener('message', handleMessage);

    // Envia heartbeat periódico para notificar outras abas que esta está ativa
    const sendHeartbeat = () => {
      const timestamp = Date.now();
      channel.postMessage({
        type: 'heartbeat',
        slug,
        sessionId,
        timestamp,
      });
    };

    // Envia heartbeat imediatamente e depois a cada 1 segundo
    sendHeartbeat();
    const heartbeatInterval = setInterval(sendHeartbeat, 1000);

    // Verifica periodicamente se outras abas ainda estão ativas
    // (caso uma aba feche sem enviar 'disconnect')
    const checkInterval = setInterval(() => {
      checkOtherTabs();
    }, 2000);

    // Cleanup: notifica outras abas que esta está saindo
    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(checkInterval);

      if (channelRef.current) {
        channelRef.current.postMessage({
          type: 'disconnect',
          slug,
          sessionId,
        });
        channelRef.current.close();
      }
    };
  }, [slug, isActive]);

  return { isAnotherTabActive };
}

